import type { Document } from '@contentful/rich-text-types';
import { BLOCKS } from '@contentful/rich-text-types';

import type {
  UniversityProgramDegreeDescriptionPageAdmissionsProcessLinks as AdmissionsProcessLinks,
  Faq,
} from 'bundles/degree-description/__generated__/baseContentfulTypes';
import type { AdmissionRequirementsSection } from 'bundles/degree-description/components/modules/program-details/AdmissionRequirementsModule';

const HEADING_NODE_TYPES: string[] = [
  BLOCKS.HEADING_1,
  BLOCKS.HEADING_2,
  BLOCKS.HEADING_3,
  BLOCKS.HEADING_4,
  BLOCKS.HEADING_5,
  BLOCKS.HEADING_6,
];

type TextNode = { value?: string; content?: TextNode[] };

const getNodeText = (node: TextNode): string => node.value ?? (node.content ?? []).map(getNodeText).join('');

const isFaq = (entry: unknown): entry is Faq =>
  typeof entry === 'object' && entry !== null && '__typename' in entry && entry.__typename === 'Faq';

/**
 * Groups the admissions process rich text into accordion sections: each heading starts a section,
 * and each embedded FAQ entry after it becomes an item in that section.
 */
const getAdmissionRequirementsSections = (
  content: Document | undefined,
  links: AdmissionsProcessLinks | undefined
): AdmissionRequirementsSection[] => {
  const sections: AdmissionRequirementsSection[] = [];
  const entries = links?.entries?.block ?? [];

  (content?.content ?? []).forEach((node) => {
    if (HEADING_NODE_TYPES.includes(node.nodeType)) {
      sections.push({ sectionTitle: getNodeText(node), items: [] });
      return;
    }

    if (node.nodeType !== BLOCKS.EMBEDDED_ENTRY) {
      return;
    }

    const entry = entries.find((candidate) => candidate?.sys.id === node.data?.target?.sys?.id);
    if (!isFaq(entry) || !entry.question || !entry.answer) {
      return;
    }

    let currentSection = sections[sections.length - 1];
    if (!currentSection) {
      currentSection = { sectionTitle: '', items: [] };
      sections.push(currentSection);
    }
    currentSection.items.push({ id: entry.sys.id, question: entry.question, answer: entry.answer });
  });

  return sections.filter((section) => section.items.length > 0);
};

export default getAdmissionRequirementsSections;
